import { forwardRef } from 'react';
import type {
  PropsWithoutRef,
  Ref,
  ReactNode,
  ChangeEventHandler,
  MouseEvent,
  KeyboardEvent
} from 'react';
import styled, { css } from 'styled-components';
import { mix } from 'polished';

import { defaultThemeProp } from '../../theme';
import type { Action, BaseProps, ForwardRefForwardPropsComponent, TestIdProp } from '../../types';
import FormField from '../FormField';
import { StyledFormControl } from '../FormControl';
import type { FormControlProps } from '../FormControl';
import { useDirection, useI18n, useTestIds, useUID } from '../../hooks';
import VisuallyHiddenText from '../VisuallyHiddenText';
import { withTestIds } from '../../utils';

import { getSelectTestIds } from './Select.test-ids';

const getSelectArrowSvg = (color: string) =>
  // cspell:disable-next-line
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 25 25' fill='${encodeURIComponent(color)}'%3E%3Cpath d='M21 8v2.75l-8.5 6.5-8.5-6.5V8l8.5 6.5z'/%3E%3C/svg%3E")`;

export interface SelectProps extends BaseProps, TestIdProp {
  /** Pass Options components as children to render the options of the Select list. */
  children: ReactNode;
  /** Called when an Option is chosen within the list. */
  onChange?: ChangeEventHandler<HTMLSelectElement>;
  /** Callback fired when AI suggestion is accepted/rejected */
  onResolveSuggestion?: FormControlProps['onResolveSuggestion'];
  /**
   * Sets DOM id for the control and associates label element via 'for' attribute.
   * If an id is not pass, a random id will be generated for any render.
   */
  id?: FormControlProps['id'];
  /** Set visual state based on a validation state. */
  status?: FormControlProps['status'];
  /** Pass a string or a fragment with an Icon and string. */
  label?: FormControlProps['label'];
  /** Visually hides the label region. */
  labelHidden?: FormControlProps['labelHidden'];
  /** It is recommended to pass a simple string to offer guidance. Text will be styled based on status prop. */
  info?: FormControlProps['info'];
  /** Indicate if the field is required. The browser defaults to false. */
  required?: FormControlProps['required'];
  /**
   * Creates a controlled input and sets the value. Requires an onChange handler to update value.
   * value + onChange is the recommended method per React team.
   */
  value?: FormControlProps['value'];
  /** Disable the control. The browser defaults to false. */
  disabled?: FormControlProps['disabled'];
  /** Makes the input non editable and non clickable. The browser defaults to false. */
  readOnly?: FormControlProps['readOnly'];
  /** Sets html name attribute for the underlying control. Useful for mapping to a data field. */
  name?: FormControlProps['name'];
  /** Pass an array of Action objects to append button(s) inline with the Select. */
  actions?: Action[];
  /** Pass a heading and content to show additional information on the field. */
  additionalInfo?: FormControlProps['additionalInfo'];
  /** A ref to the select element. */
  ref?: Ref<HTMLSelectElement>;
}

export const StyledSelect = styled.select<{
  hasStatus: boolean;
  readOnly: boolean;
}>(({ theme, readOnly, hasStatus }) => {
  const {
    base,
    components: {
      'form-control': { 'foreground-color': fg, 'background-color': backgroundColor },
      select,
      icon
    }
  } = theme;
  const { rtl } = useDirection();

  return css`
    appearance: none;
    -webkit-appearance: none;
    overflow-x: hidden;
    text-overflow: ellipsis;
    width: 100%;
    height: ${select.height};
    min-height: ${base['hit-area']['mouse-min']};
    padding-inline-start: ${select.padding};
    padding-inline-end: calc(4 * ${base.spacing});
    background-image: ${getSelectArrowSvg(fg)};
    background-repeat: no-repeat;
    background-size: ${icon.size.s} ${icon.size.s};
    background-position: ${rtl ? '0.375rem' : 'calc(100% - 0.375rem)'} 50%;
    border-width: ${select['border-width']};
    border-radius: calc(${base['border-radius']} * ${select['border-radius']});

    @media (forced-colors: active) {
      background-image: ${getSelectArrowSvg('CanvasText')};
    }

    ${readOnly &&
    css`
      background-image: none;
    `}

    ${!hasStatus &&
    css`
      border-color: ${select['border-color']};
    `}

    &:has(option[value='']:checked, option:not([value]):checked) {
      color: ${mix(base.transparency['transparent-3'], fg, backgroundColor)};
    }

    & option {
      color: ${fg};

      &[value=''],
      &:not([value]) {
        color: ${mix(base.transparency['transparent-3'], fg, backgroundColor)};
      }
    }

    &::-ms-expand {
      display: none;
    }

    @media (pointer: coarse) {
      min-height: ${base['hit-area']['finger-min']};
    }
  `;
});

StyledSelect.defaultProps = defaultThemeProp;

const Select: ForwardRefForwardPropsComponent<SelectProps> = forwardRef(function Select(
  props: PropsWithoutRef<SelectProps>,
  ref: SelectProps['ref']
) {
  const uid = useUID();
  const t = useI18n();

  const {
    testId,
    additionalInfo,
    children,
    id = uid,
    label,
    labelHidden,
    info,
    status,
    required = false,
    disabled = false,
    readOnly = false,
    actions,
    onResolveSuggestion,
    ...restProps
  } = props;

  const testIds = useTestIds(testId, getSelectTestIds);

  const Comp = (
    <StyledFormControl
      data-testid={testIds.control}
      autoComplete='_off'
      {...restProps}
      as={StyledSelect}
      hasSuggestion={status === 'pending' && !!onResolveSuggestion}
      id={id}
      hasStatus={!!status}
      status={status}
      required={required}
      disabled={disabled}
      label={label}
      readOnly={readOnly}
      ref={ref}
      onMouseDown={
        readOnly
          ? (e: MouseEvent<HTMLSelectElement>) => {
              e.preventDefault();
            }
          : undefined
      }
      onKeyDown={
        readOnly
          ? (e: KeyboardEvent) => {
              // TODO: Consideration is needed for key modifiers, e.g. "ctrl p"
              if (!['Escape', 'Tab', 'Space'].includes(e.key)) e.preventDefault();
            }
          : undefined
      }
    >
      {children}
    </StyledFormControl>
  );

  return label ? (
    <FormField
      testId={testIds}
      label={
        <>
          {label}
          {readOnly && <VisuallyHiddenText>{` ${t('read_only')}`}</VisuallyHiddenText>}
        </>
      }
      labelHidden={labelHidden}
      id={id}
      info={info}
      status={status}
      required={required}
      disabled={disabled}
      readOnly={readOnly}
      actions={actions}
      onResolveSuggestion={onResolveSuggestion}
      additionalInfo={additionalInfo}
    >
      {Comp}
    </FormField>
  ) : (
    Comp
  );
});

export default withTestIds(Select, getSelectTestIds);
